import { IContextualMenuItem } from "@fluentui/react";

type MenuItemOrNull = IContextualMenuItem | null | undefined;

/**
 * Pure menu assembly for the cross-library "All documents" view, following the
 * buildMenuItems contract: slots hold already-computed menu items (null = hidden),
 * assembly only decides ordering/inclusion.
 *
 * Ordering mirrors the single-library view's buildMenuItems POSITION FOR POSITION so
 * the two grids feel like one product:
 *  - bar: open commands → share/shareWithGroup/copyLink → rename/replace → download → PDF →
 *    HIX upload → automation/signing → copy/move → delete → versionHistory →
 *    checkout state machine → quick filter last. Preview/properties are NOT bar commands — they appear as
 *    icon-only FAR items, and shareToTeams is context-menu-only, exactly like the
 *    library view.
 *  - context menu: shareToTeams first (Teams mode), preview after open, properties
 *    last. The checkout state machine is grouped under the "More" submenu (the
 *    `more` slot), exactly like the library view; only the BAR keeps it flat.
 *  - far items: selectedFileCount first, as in the library; then switchToLocations
 *    (the deliberate deviation from the mirror: the view switcher is a labeled
 *    dropdown à la SharePoint's view selector, so it leads the COMMANDS rather than
 *    sitting near the tail), then clearFilter → propertiesIcon → previewIcon →
 *    columns → filter → refresh.
 *
 * Overflow is Fluent CommandBar's built-in responsive behavior (items that don't fit
 * collapse from the END into its "…" menu, re-measured on resize) — no static fold.
 *
 * The aggregate view is a different command SCOPE than the single-library grid:
 * folder-scoped commands (new/upload/move-to DESTINATION mechanics) have no
 * current-folder context and never appear — move/copy here only STARTS move mode.
 */

export interface AggregateMenuSlots {
    open?: MenuItemOrNull;
    preview?: MenuItemOrNull;
    previewIconOnly?: MenuItemOrNull;
    properties?: MenuItemOrNull;
    propertiesIconOnly?: MenuItemOrNull;
    copyLink?: MenuItemOrNull;
    rename?: MenuItemOrNull;
    replace?: MenuItemOrNull;
    share?: MenuItemOrNull;
    shareWithGroup?: MenuItemOrNull;
    shareToTeams?: MenuItemOrNull;
    download?: MenuItemOrNull;
    hixUpload?: MenuItemOrNull;
    delete?: MenuItemOrNull;
    checkout?: MenuItemOrNull;
    checkin?: MenuItemOrNull;
    discardCheckout?: MenuItemOrNull;
    publish?: MenuItemOrNull;
    unpublish?: MenuItemOrNull;
    versionAppRej?: MenuItemOrNull;
    // Pre-grouped submenu of the six state-machine slots above — context menu only.
    more?: MenuItemOrNull;
    versionHistory?: MenuItemOrNull;
    downloadAsPDF?: MenuItemOrNull;
    saveAsPDF?: MenuItemOrNull;
    automation?: MenuItemOrNull;
    signing?: MenuItemOrNull;
    moveTo?: MenuItemOrNull;
    copyTo?: MenuItemOrNull;
    // Inline quick-filter box (library-view pattern): rendered as the LAST bar
    // item, never in the context menu.
    quickFilter?: MenuItemOrNull;
    // view-scoped (far items)
    /**
     * "N selected" with a dismiss icon that clears the selection. First in the far
     * cluster, as in the library view — it is a status readout, not a command, and the
     * library puts it ahead of everything else there.
     */
    selectedFileCount?: MenuItemOrNull;
    filter?: MenuItemOrNull;
    clearFilter?: MenuItemOrNull;
    columns?: MenuItemOrNull;
    refresh?: MenuItemOrNull;
    switchToLocations?: MenuItemOrNull;
}

export interface AggregateMenuResult {
    commandBarItems: IContextualMenuItem[];
    commandBarFarItems: IContextualMenuItem[];
    contextMenu: IContextualMenuItem[];
}

const pushItem = (items: IContextualMenuItem[], item?: MenuItemOrNull): void => {
    if (item) {
        items.push(item);
    }
};

const compact = (items: MenuItemOrNull[]): IContextualMenuItem[] =>
    items.filter(Boolean) as IContextualMenuItem[];

export const buildAggregateMenuItems = (slots: AggregateMenuSlots): AggregateMenuResult => {
    // Shared middle section, identical in bar and context menu (library order). The
    // state machine is NOT shared: flat on the bar, grouped under `more` in the
    // context menu — exactly the library's buildMenuItems split.
    const shared: MenuItemOrNull[] = [
        slots.share, slots.shareWithGroup, slots.copyLink, slots.rename, slots.replace,
        // The library's buildMenuItems positions: PDF directly after download, then
        // HIX with automation and signing DIRECTLY behind it, ahead of copy/move.
        slots.download, slots.downloadAsPDF, slots.saveAsPDF,
        slots.hixUpload, slots.automation, slots.signing,
        slots.copyTo, slots.moveTo, slots.delete,
        slots.versionHistory,
    ];
    const stateMachine: MenuItemOrNull[] = [
        slots.checkout, slots.checkin, slots.discardCheckout,
        slots.publish, slots.unpublish, slots.versionAppRej,
    ];

    const commandBarItems: IContextualMenuItem[] = compact([
        slots.open,
        ...shared,
        ...stateMachine,
    ]);
    pushItem(commandBarItems, slots.quickFilter);

    const contextMenu: IContextualMenuItem[] = compact([
        slots.shareToTeams,
        slots.open, slots.preview,
        ...shared,
        slots.more,
        slots.properties,
    ]);

    const commandBarFarItems: IContextualMenuItem[] = compact([
        slots.selectedFileCount,
        slots.switchToLocations,
        slots.clearFilter,
        slots.propertiesIconOnly, slots.previewIconOnly,
        slots.columns,
        slots.filter, slots.refresh,
    ]);

    return { commandBarItems, commandBarFarItems, contextMenu };
};
